import { AfterViewInit, Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, switchMap, tap } from 'rxjs';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { WidgetMessageService } from '../../core/services/widget-message.service';
import { ConsumerEntityService } from '../services/consumer.entity.service';
import { ConsumerEntity } from '../consumer.entity';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { map } from 'rxjs/operators';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { makeQueryKey } from '../../utils/entity-states/query-key';
import { getMinFilterLengthText } from '../../core/util';
import { CommonModule } from '@angular/common';
import { FormDialogConsumerComponent } from '../form-dialog-consumer/form-dialog-consumer.component';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData
} from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalStorageService } from '../../core/services/local-storage.service';

@Component({
  selector: 'pixl-main-consumer',
  imports: [
    CommonModule,
    MatIcon,
    MatSortModule,
    MatIconButton,
    MatTableModule,
    MatDialogModule,
    MatButtonModule,
    MatDialogModule,
    FreeFilterInputComponent,
    TranslocoModule,
    MatTooltip,
    MatPaginator
  ],
  templateUrl: './main-consumer.component.html',
  styleUrl: './main-consumer.component.scss'
})
export class MainConsumerComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private widgetService = inject(WidgetMessageService);
  private consumerService = inject(ConsumerEntityService);
  private dialog = inject(MatDialog);
  private refresh$ = new BehaviorSubject<void>(undefined);
  private localStorageService = inject(LocalStorageService);

  consumers$!: Observable<ConsumerEntity[]>;
  deletedIds$ = new BehaviorSubject<string[]>([]);

  public readonly filterLen = 3;
  private nameFilter = '';
  private urlFilter = '';
  public currentSelectedRow = signal<string | undefined>(undefined);
  public displayedColumns: string[] = ['actions', 'id', 'name', 'url', 'description'];
  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  pageSize = 25;
  pageIndex = 0;
  totalItems = 1000;

  private destroyRef = inject(DestroyRef);
  private pageChange$ = new BehaviorSubject<{ pageIndex: number, pageSize: number }>({ pageIndex: this.pageIndex, pageSize: this.pageSize });

  ngOnInit() {
    this.consumers$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': 'name'
        };

        if (this.nameFilter?.trim()?.length >= this.filterLen) {
          query['page[number]'] = 0;
          query['filter[nameLike]'] = this.nameFilter.trim();
        }

        if (this.urlFilter?.trim()?.length >= this.filterLen) {
          query['page[number]'] = 0;
          query['filter[urlLike]'] = this.urlFilter.trim();
        }

        return from(this.consumerService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.consumerService.allByQuery(query))),
          map((consumers: ConsumerEntity[]) => consumers.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.consumerService.stateClass) as EntityStateModel<ConsumerEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit() {
    this.paginator.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  add(): void {
    const dialogRef = this.dialog.open(FormDialogConsumerComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: {}
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<ConsumerEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new ConsumerEntity(), result);
        await this.consumerService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.widgetService.setMessage({
          message: 'Anwendung erfolgreich erstellt',
          messageTye: 'mtSuccess'
        });
      }
    });
  }

  edit(consumer: ConsumerEntity) {
    this.currentSelectedRow.set(consumer.id);
    const dialogRef = this.dialog.open(FormDialogConsumerComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: { ...consumer }
    });

    dialogRef.afterClosed().subscribe(async (updatedConsumer: ConsumerEntity | undefined) => {
      if (!updatedConsumer) return;
      await firstValueFrom(this.consumerService.update(updatedConsumer));
      this.refresh$.next();

      this.widgetService.setMessage({
        message: 'Anwendung erfolgreich editiert',
        messageTye: 'mtSuccess'
      });
    });
  }

  async delete(consumer: ConsumerEntity) {
    this.currentSelectedRow.set(consumer.id);
    if (!consumer?.id) {
      console.error('ID is undefined!', consumer);
      return;
    }

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(consumer.name),
            headerText: 'Anwendung löschen',
            afterNameText: 'wirklich löschen?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.consumerService.delete(consumer.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: 'Anwendung erfolgreich gelöscht.',
      messageTye: 'mtSuccess'
    });
  }

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.filterLen);
  }

  public setNameFilter(input: string): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.nameFilter = input;
    this.refresh$.next();
  }

  public setUrlFilter(input: string): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.urlFilter = input;
    this.refresh$.next();
  }

  trackById(index: number, consumer: ConsumerEntity): string {
    return consumer.id;
  }
}
